import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DataSuccessModal from "./DataSucces";
import config from "../../config.js";
import { useUser } from "../../pages/login/UserContextPersistent";
import LocationPin from "../../images/LocationPin.svg";

export default function RegisterNeedInfo() {
  const navigate = useNavigate();
  const { state } = useUser(); // Access user state from context
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [saveDraftButtonText, setSaveDraftButtonText] = useState("Save Draft");

  const initialFormData = {
    totalVehicles: "",
    totalStops: "",
    totalRoutes: "",
    totalSites: "",
    totalDrivers: "",
    totalUsers: "",
    dataRetention: "30",
    dataImportExport: "pdf",
    trackingFrequency: "5",
    displayScreenType: "led",
    goLiveDate: "",
    networkRequirement: {
      wifi: false,
      cellular: false,
      ethernet: false,
    },
    hasCloudInfrastructure: "",
    cloudDetails: "",
    needsSupport: "",
    specificNeeds: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  // Fetch organizationId from localStorage/context on mount
  useEffect(() => {
    const userState = localStorage.getItem("userState");
    if (userState) {
      const parsedUserState = JSON.parse(userState);
      if (parsedUserState?.user?.organizationId) {
        setOrgId(parsedUserState.user.organizationId);
      } else {
        console.error("No organizationId found in localStorage");
        navigate("/registerMoreDetails"); // Redirect if no orgId
      }
    } else {
      console.error("No userState found in localStorage");
      navigate("/register"); // Redirect to initial registration if no user state
    }
  }, [navigate]);

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#183059", borderRadius: "8px" },
      "&:hover fieldset": { borderColor: "#183059" },
      "&.Mui-focused fieldset": { borderColor: "#183059" },
    },
  };

  const labelStyle = {
    width: "50%",
    fontWeight: "500",
    fontSize: "0.9rem",
    pr: 1,
    color: "#333",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      networkRequirement: {
        ...prev.networkRequirement,
        [name]: checked,
      },
    }));
  };

  const transformFormData = () => {
    const screenTypeMap = {
      led: "LED",
      lcd: "LCD",
      none: "NODISPLAY",
    };
    const dataImportType = formData.dataImportExport
      ? formData.dataImportExport.toUpperCase()
      : null;

    const baseData = {
      organizationId: orgId, // Use dynamic orgId from localStorage
      totalNoOfVehicles: parseInt(formData.totalVehicles) || 0,
      totalNoOfStops: parseInt(formData.totalStops) || 0,
      totalNoOfRoutes: parseInt(formData.totalRoutes) || 0,
      totalNoOfSites: parseInt(formData.totalSites) || 0,
      totalNoOfDrivers: parseInt(formData.totalDrivers) || 0,
      totalNoOfSysUsers: parseInt(formData.totalUsers) || 0,
      retentionPeriod: parseInt(formData.dataRetention),
      trackingFreq: parseInt(formData.trackingFrequency),
      screenType: screenTypeMap[formData.displayScreenType],
      goLiveDate: formData.goLiveDate
        ? new Date(formData.goLiveDate).toISOString()
        : null,
      ownCloudInfra: formData.hasCloudInfrastructure === "yes",
      ownCloudDetails:
        formData.hasCloudInfrastructure === "yes"
          ? formData.cloudDetails
          : "",
      maintainenceSupprt: formData.needsSupport === "yes",
      tellUsMore: formData.specificNeeds,
    };

    const networkRequirements = Object.entries(formData.networkRequirement)
      .filter(([_, checked]) => checked)
      .map(([name]) => name.toUpperCase());

    return {
      ...baseData,
      networkRequirements,
      dataImportType,
    };
  };

  const submitFormData = async () => {
    if (!orgId) {
      console.error("No organizationId available for submission");
      return;
    }
    setLoading(true);
    try {
      const apiData = transformFormData();
      console.log("Frontend payload:", JSON.stringify(apiData, null, 2));

      const apiUrl = `${config.baseURL}/requirements`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Ensure token exists
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `API error: ${response.status} - ${
            errorData.message || "Unknown error"
          }`
        );
      }

      const result = await response.json();
      console.log("API response:", result);

      setSaveSuccess(true);
      setTimeout(() => {
        setLoading(false); // Turn off loading only after navigation
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
      setErrors({ submit: "Failed to submit requirements. Please try again." });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const numericFields = [
      "totalVehicles",
      "totalStops",
      "totalRoutes",
      "totalSites",
      "totalDrivers",
      "totalUsers",
    ];

    numericFields.forEach((field) => {
      // if (!formData[field]) {
      if (formData[field] !== "" && !/^\d+$/.test(formData[field])) {
        //   newErrors[field] = 'This field is required';
        // } else if (isNaN(formData[field]) || parseInt(formData[field]) < 0) {
        newErrors[field] = "Please enter a valid non-negative number";
      }
    });

    if (formData.goLiveDate) {
      //   newErrors.goLiveDate = 'Please select a go-live date';
      // } else {
      const selectedDate = new Date(formData.goLiveDate);
      const today = new Date();
      if (selectedDate <= today) {
        newErrors.goLiveDate = "Go-live date must be in the future";
      }
    }

    // const { wifi, cellular, ethernet } = formData.networkRequirement;
    // if (!wifi && !cellular && !ethernet) {
    //   newErrors.networkRequirement = 'Please select at least one network requirement';
    // }

    // if (!formData.hasCloudInfrastructure) {
    //   newErrors.hasCloudInfrastructure = 'Please select an option';
    // } else
    if (formData.hasCloudInfrastructure === "yes" && !formData.cloudDetails) {
      newErrors.cloudDetails =
        "Please provide details about your cloud infrastructure";
    }

    // if (!formData.needsSupport) {
    //   newErrors.needsSupport = 'Please select an option';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      console.log("Form is valid. Submitting data:", transformFormData());
      await submitFormData();
    } else {
      console.log("Form has validation errors");
    }
  };

  const handleSaveDraft = () => {
    setSaveDraftButtonText("Saving...");
    console.log("Saving draft:", formData);
    localStorage.setItem("requirementDraft", JSON.stringify(formData));
    setSaveDraftButtonText("Saved");
    // Revert to "Save Draft" after 2 seconds
    setTimeout(() => {
      setSaveDraftButtonText("Save Draft");
    }, 2000);
  };

  const FormField = ({ label, width = "50%", children, required, error }) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <InputLabel sx={{ ...labelStyle, width }}>
        {label}
        {required && (
          <Box component="span" sx={{ ml: 1, color: "#EE3B2B" }}>
            *
          </Box>
        )}
      </InputLabel>
      <Box sx={{ width }}>
        {children}
        {error && (
          <Typography color="error" variant="caption">
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // if (saveSuccess) {
  //   return <DataSuccessModal />;
  // }

  if (!orgId) {
    return <Typography>Loading organization data...</Typography>;
  }

  return (
    <Box
      sx={{
        p: { xs: "12px", sm: "18px", md: "32px" },
        backgroundColor: "#EEEEEE",
        minHeight: "100vh",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: "1360px",
          borderRadius: "20px",
          p: { xs: "16px", sm: "24px", md: "32px", lg: "40px 80px" },
          background: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              textAlign: "center",
              fontFamily: "Roboto",
              fontSize: "36px",
              color: "#183059",
            }}
          >
            Tell Us More About Your Need
          </Typography>
          <Typography sx={{ mb: 2, textAlign: "center", color: "#1F242E" }}>
            Help us understand your requirements to tailor RouteEye to your
            needs.
            <br />
            <strong style={{ fontWeight: 600 }}>Note:</strong>{" "}
            <em>
              These Details Can't Be Changed Later, So Enter Details Carefully.
            </em>
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid container spacing={3}>
            {[
              {
                name: "totalVehicles",
                label: "Total No. of vehicles to track",
                placeholder: "Enter number of vehicles",
              },
              {
                name: "totalStops",
                label: "Total No. of stops to track",
                placeholder: "Enter number of stops",
              },
              {
                name: "totalRoutes",
                label: "Total No. of routes to track",
                placeholder: "Enter number of routes",
              },
              {
                name: "totalSites",
                label: "Total No. of sites to manage",
                placeholder: "Enter number of sites",
              },
              {
                name: "totalDrivers",
                label: "Total No. of drivers to manage",
                placeholder: "Enter number of drivers",
              },
              {
                name: "totalUsers",
                label: "Total No. of system users",
                placeholder: "Enter number of system users",
              },
            ].map((field) => (
              <Grid item xs={12} md={6} key={field.name}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <InputLabel sx={{ ...labelStyle }}>
                    {field.label}
                    {/* <Box component="span" sx={{ ml: 1, color: "#EE3B2B" }}>*</Box> */}
                  </InputLabel>
                  <Box sx={{ width: "50%" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      sx={{ width: "100%", ...inputStyle }}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      onKeyDown={(e) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          e.key !== "Backspace" &&
                          e.key !== "Tab" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}

            <Grid item xs={12} md={6}>
              <FormField label="Data retention period">
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    size="small"
                    name="dataRetention"
                    value={formData.dataRetention}
                    onChange={handleChange}
                    sx={inputStyle}
                    displayEmpty
                    renderValue={(value) =>
                      value ? `${value} days` : "Select retention period"
                    }
                  >
                    <MenuItem value="30">30 days</MenuItem>
                    <MenuItem value="60">60 days</MenuItem>
                    <MenuItem value="120">120 days</MenuItem>
                  </Select>
                </FormControl>
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                label={
                  <>
                    Data Import/Export <br />
                    (PDF/Excel/CSV)
                  </>
                }
              >
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    size="small"
                    name="dataImportExport"
                    value={formData.dataImportExport}
                    onChange={handleChange}
                    sx={inputStyle}
                    displayEmpty
                    renderValue={(value) => value || "Select format"}
                  >
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField label="Tracking frequency">
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    size="small"
                    name="trackingFrequency"
                    value={formData.trackingFrequency}
                    onChange={handleChange}
                    sx={inputStyle}
                    displayEmpty
                    renderValue={(value) =>
                      value ? `${value} seconds` : "Select frequency"
                    }
                  >
                    <MenuItem value="5">5 seconds</MenuItem>
                    <MenuItem value="10">10 seconds</MenuItem>
                  </Select>
                </FormControl>
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                label={
                  <Typography component="span">
                    Display screen type <br /> (LED/LCD/No display)
                    <img src={LocationPin} alt="" />
                  </Typography>
                }
              >
                <FormControl sx={{ width: "100%" }}>
                  <Select
                    size="small"
                    name="displayScreenType"
                    value={formData.displayScreenType}
                    onChange={handleChange}
                    sx={inputStyle}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) return "Select screen type";
                      const options = { led: "LED", lcd: "LCD", none: "NONE" };
                      return options[value];
                    }}
                  >
                    <MenuItem value="led">LED</MenuItem>
                    <MenuItem value="lcd">LCD</MenuItem>
                    <MenuItem value="none">No Display</MenuItem>
                  </Select>
                </FormControl>
              </FormField>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <InputLabel
                  sx={{ width: { xs: "50%", md: "30%" }, ...labelStyle }}
                >
                  When do you want system to Go-Live?
                </InputLabel>
                <Box
                  sx={{ position: "relative", width: { xs: "50%", md: "20%" } }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    type="date"
                    name="goLiveDate"
                    value={formData.goLiveDate}
                    onChange={handleChange}
                    error={!!errors.goLiveDate}
                    helperText={errors.goLiveDate}
                    sx={{ width: "100%", ...inputStyle }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <InputLabel
                  sx={{
                    width: { xs: "50%", md: "20%" },
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    pr: 1,
                    mt: 1,
                    color: "#333",
                  }}
                >
                  Network requirement {""}
                  <img src={LocationPin} alt="" />
                  <img src={LocationPin} alt="" />
                </InputLabel>
                <Box sx={{ width: { xs: "50%", md: "80%" } }}>
                  <FormGroup row>
                    {["GSM", "GPS", "Satellite"].map((option) => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            name={option}
                            checked={formData.networkRequirement[option]}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={option.charAt(0).toUpperCase() + option.slice(1)}
                        sx={{
                          "&.Mui-checked": {
                            color: "#20A8E0",
                          },
                        }}
                      />
                    ))}
                  </FormGroup>
                  {errors.networkRequirement && (
                    <Typography color="error" variant="caption">
                      {errors.networkRequirement}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <InputLabel
                  sx={{ width: { xs: "100%", md: "50%" }, ...labelStyle }}
                >
                  Do you have your own cloud infrastructure?
                </InputLabel>
                <Box>
                  <RadioGroup
                    row
                    name="hasCloudInfrastructure"
                    value={formData.hasCloudInfrastructure}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      sx={{ mr: 4 }}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                  {errors.hasCloudInfrastructure && (
                    <Typography color="error" variant="caption">
                      {errors.hasCloudInfrastructure}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {formData.hasCloudInfrastructure === "yes" && (
              <Grid item xs={12}>
                <Box sx={{ pl: { xs: 0, md: "50%" } }}>
                  <InputLabel
                    sx={{
                      fontWeight: "500",
                      fontSize: "0.9rem",
                      mb: 0.5,
                      color: "#333",
                    }}
                  >
                    Tell us more about it:
                  </InputLabel>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="cloudDetails"
                    value={formData.cloudDetails}
                    onChange={handleChange}
                    error={!!errors.cloudDetails}
                    helperText={errors.cloudDetails}
                    placeholder="Please provide details about your cloud infrastructure"
                    sx={inputStyle}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <InputLabel
                  sx={{
                    width: { xs: "50%", md: "50%" },
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    pr: 1,
                    mt: 1,
                    color: "#333",
                  }}
                >
                  Do you need post Go-Live maintenance & support?
                </InputLabel>
                <Box sx={{ width: { xs: "50%", md: "50%" } }}>
                  <RadioGroup
                    row
                    name="needsSupport"
                    value={formData.needsSupport}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                      sx={{ mr: 4 }}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                  {errors.needsSupport && (
                    <Typography color="error" variant="caption">
                      {errors.needsSupport}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <InputLabel
                  sx={{
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    mb: 1,
                    color: "#333",
                  }}
                >
                  Tell us more about your specific needs that we didn't ask!
                </InputLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  name="specificNeeds"
                  value={formData.specificNeeds}
                  onChange={handleChange}
                  placeholder="Enter details of your specific needs"
                  sx={inputStyle}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              {[
                "Screens should be procured by you, RouteEye will provide the application integration.",
                "You have to procure the GSM SIM cards of any local mobile operator.",
              ].map((note, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    mb: index < 2 ? 1 : 0,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Box component="span" sx={{ mr: 1, color: "#EE3B2B" }}>
                    {index < 1 ? (
                      <img src={LocationPin} alt="" />
                    ) : (
                      <>
                        <img src={LocationPin} alt="" />
                        <img src={LocationPin} alt="" />
                      </>
                    )}
                  </Box>
                  <Box component="span">{note}</Box>
                </Typography>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 2,
            mb: 1,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            sx={{
              width: "240px",
              height: "55px",
              borderRadius: "8px",
              borderWidth: 1,
              fontSize: "20px",
              padding: "16px 24px",
              border: "1px solid #414958",
              backgroundColor: "#FFFFFF",
              color: "#111214",
              fontWeight: 600,
              textTransform: "none",
            }}
            onClick={handleSaveDraft}
            disabled={saveDraftButtonText === "Saving..."}
          >
            {saveDraftButtonText === "Saving..." && (
              <CircularProgress size={24} sx={{ mr: 1 }} />
            )}
            {saveDraftButtonText}
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{
              width: "240px",
              height: "55px",
              borderRadius: "8px",
              borderWidth: 1,
              padding: "16px 24px",
              backgroundColor: "#F9E719",
              color: "#111214",
              fontSize: "20px",
              fontWeight: 600,
              border: "1px solid #414958",
              "&:hover": { background: "#F9E719" },
              textTransform: "none",
            }}
            onClick={handleSubmit}
            disabled={loading || !orgId}
          >
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
